/**
 * ProcessCloseCard.tsx - P4-S8 step 27 (Prozessabschluss), mounted inside the S7
 * VersichererBenachrichtigungenPanel (which holds the persisted mandate id from the dialog read), and the
 * Brokermandat's step 31 in the area «Mandate & Eintragungen»: there the open items are the rows not yet
 * decided and the scope insurers without any Meldung (named, with «Meldung auslösen»).
 *
 * Close and reopen need mandate.eingangskontrolle (their routes); «Meldung auslösen» needs
 * communication.send and mandate.eingangskontrolle.
 *
 * Reads GET /mandates/:id/process-state. `can_close` -> «Prozess abschliessen»; `requires_reason`
 * (open relationships / notifications) -> a mandatory reason dialog before the close; otherwise one
 * click. A closed process shows the Abschluss line + «Wieder öffnen», and «Nachmeldung offen» when a
 * late relationship is still open (Use Case 11 step 2). S1's Mandatskachel is NOT changed. The state is a
 * query under the Versicherer-Benachrichtigung prefix, so a send or a «schliessen» in the panel refreshes it.
 */

import { useState } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { dateShortDe } from '../../lib/format';
import { useEffectivePermissions } from '../administration/useEffectivePermissions';
import { useMandateProcessAction, useMandateProcessState } from './versicherer-benachrichtigung-queries';
import { useFollowRegistrationJob, useRegistrationDecision } from './mandate-registration-queries';
import { RegistrationSendingLine } from './MandateRegistrationPanel';

const REASON_MAX = 1000;
/** The key of the close and reopen routes. */
export const PROCESS_CLOSE_KEY = 'mandate.eingangskontrolle';
/** The keys of «Meldung auslösen» (it mails the insurers). */
const RUN_KEYS = ['communication.send', 'mandate.eingangskontrolle'] as const;

export function ProcessCloseCard({
  mandateId,
  kind = 'analyse',
  subject,
}: {
  mandateId: string;
  kind?: 'analyse' | 'broker';
  /** The customer (a Brokermandat's «Meldung auslösen» refreshes its reads). */
  subject?: { subjectType: 'person' | 'firma'; subjectId: string };
}) {
  const stateQuery = useMandateProcessState(mandateId);
  const action = useMandateProcessAction(mandateId);
  const run = useRegistrationDecision(mandateId, subject ?? { subjectType: 'firma', subjectId: '' });
  // A Brokermandat's «Meldung auslösen» here is followed like the panel's: the card refreshes once it lands.
  const sending = useFollowRegistrationJob(mandateId, subject ?? { subjectType: 'firma', subjectId: '' }, {
    enabled: kind === 'broker' && subject !== undefined,
  });
  const { can } = useEffectivePermissions();
  const canClose = can(PROCESS_CLOSE_KEY);
  const canRun = RUN_KEYS.every((k) => can(k));
  const title = kind === 'broker' ? 'Brokermandat-Prozess' : 'Analysemandat-Prozess';
  const [reasonOpen, setReasonOpen] = useState(false);
  const [reason, setReason] = useState('');
  const busy = action.isPending;

  function doClose(withReason: boolean) {
    action.mutate(
      { action: 'close', body: withReason ? { reason } : {} },
      {
        onSuccess: () => {
          setReasonOpen(false);
          setReason('');
        },
      },
    );
  }

  if (stateQuery.isError) {
    return (
      <p className="flex items-center gap-2 text-sm text-destructive" role="alert">
        <AlertTriangle className="size-4" /> Der Prozessstatus konnte nicht geladen werden.
      </p>
    );
  }
  const state = stateQuery.data;
  if (state === undefined) {
    return (
      <p className="flex items-center gap-2 text-sm text-muted-foreground" role="status">
        <Loader2 className="size-4 animate-spin" /> Lädt ...
      </p>
    );
  }
  const actionError = action.isError
    ? action.variables?.action === 'reopen'
      ? 'Der Prozess konnte nicht wieder geöffnet werden.'
      : 'Der Prozess konnte nicht abgeschlossen werden.'
    : null;
  return (
    <section
      className="flex flex-col gap-2 rounded-lg border bg-card p-4"
      aria-label={title}
      data-testid="process-close-card"
    >
      <h3 className="text-sm font-semibold">{title}</h3>
      {actionError !== null && (
        <p className="flex items-center gap-2 text-sm text-destructive" role="alert" data-testid="pcc-error">
          <AlertTriangle className="size-4" /> {actionError}
        </p>
      )}
      {state.status === 'abgeschlossen' ? (
        <div className="flex flex-col gap-2">
          <p className="text-sm" data-testid="pcc-closed">
            Abgeschlossen · {dateShortDe(state.closed_at)}
            {state.closed_by_name ? ` · ${state.closed_by_name}` : ''}
          </p>
          {state.late_relationship_count > 0 && (
            <p className="text-xs text-sem-decide" data-testid="pcc-nachmeldung">
              Nachmeldung offen: {state.late_relationship_count} neu
            </p>
          )}
          {canClose && (
            <Button size="sm" variant="outline" disabled={busy} onClick={() => action.mutate({ action: 'reopen' })} data-testid="pcc-reopen">
              Wieder öffnen
            </Button>
          )}
        </div>
      ) : reasonOpen ? (
        <div className="flex flex-col gap-2">
          <p className="text-xs text-muted-foreground" data-testid="pcc-reason-why">
            {kind === 'broker'
              ? `${state.open_notification_count} ohne Entscheid · ${state.unreported_carrier_count ?? 0} nicht gemeldet${(state.missing_carrier_uid_count ?? 0) > 0 ? ' · Versicherer-UID fehlt' : ''}`
              : `${state.open_relationship_count} nicht benachrichtigt · ${state.open_notification_count} ohne Rückmeldung`}
          </p>
          <textarea
            className="min-h-16 rounded border px-2 py-1 text-sm"
            placeholder="Begründung"
            value={reason}
            maxLength={REASON_MAX}
            onChange={(e) => setReason(e.target.value)}
            aria-label="Begründung"
            data-testid="pcc-reason-input"
          />
          <div className="flex gap-2">
            <Button
              size="sm"
              disabled={busy || reason.trim() === ''}
              onClick={() => doClose(true)}
              data-testid="pcc-reason-submit"
            >
              Prozess abschliessen
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setReasonOpen(false)}>
              Abbrechen
            </Button>
          </div>
        </div>
      ) : state.can_close && canClose ? (
        <Button
          size="sm"
          disabled={busy}
          onClick={() => (state.requires_reason ? setReasonOpen(true) : doClose(false))}
          data-testid="pcc-close"
        >
          Prozess abschliessen
        </Button>
      ) : state.can_close ? null : (
        <p className="text-xs text-muted-foreground" data-testid="pcc-not-closable">
          Der Prozess kann noch nicht abgeschlossen werden.
        </p>
      )}
      {kind === 'broker' && state.status !== 'abgeschlossen' && (state.unreported_carriers ?? []).length > 0 && (
        // Step 31: an insurer of the scope without any Meldung is an open item; «Meldung auslösen» reports it.
        <div className="flex flex-col gap-1" data-testid="pcc-unreported">
          <p className="text-xs text-sem-decide">
            Nicht gemeldet: {(state.unreported_carriers ?? []).map((u) => u.carrier_name ?? u.carrier_uid ?? u.carrier_key).join(', ')}
          </p>
          {canRun && (
            <Button size="sm" variant="outline" disabled={run.isPending} onClick={() => run.mutate({ action: 'run' }, { onSettled: () => void stateQuery.refetch() })} data-testid="pcc-run">
              Meldung auslösen
            </Button>
          )}
        </div>
      )}
      {sending && <RegistrationSendingLine follow={sending} testId="pcc-sending" />}
      {kind === 'broker' && state.status !== 'abgeschlossen' && (state.missing_carrier_uid_count ?? 0) > 0 && (
        // Policies of the scope without insurer UID name no insurer: one open item, set the UID on the policy.
        <p className="text-xs text-sem-decide" data-testid="pcc-missing-uid">
          Versicherer-UID fehlt ({state.missing_carrier_uid_count} {state.missing_carrier_uid_count === 1 ? 'Police' : 'Policen'})
        </p>
      )}
    </section>
  );
}
